import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaPlay, FaExclamationTriangle, FaQuoteLeft, FaCheckCircle, FaArrowRight, FaTimesCircle, FaTimes, FaUndo } from "react-icons/fa";
import EmptyState from "../EmptyState";
import { colors, spacing, typography, radius, btnGhost, transitions } from "../../utils/designTokens";
import { actaAdmin, mensajeDeError, urlDescargable } from "../../utils/asambleas/api";
import { pad2 } from "../../utils/actaConfig";
import {
  marcasGenerales,
  marcasPorPunto,
  evidenciasDePunto,
  etiquetaCampo,
  formatoTiempo,
  totalMarcas,
  estadoDeMarca,
  marcaCerrada,
  FILTROS_MARCAS,
} from "./actaFlujo";

// Panel de revisión del acta redactada por la IA: marcas para revisar (por
// punto y generales) y las citas textuales de la transcripción en las que se
// apoyó, cada una con "Escuchar" en su momento exacto del audio.
//
// El audio se pide al servidor (actaAdmin audioUrl → URL firmada de 10 min);
// se reusa mientras está vigente y nunca se guarda ni se loguea.
//
// Props:
//   acta        data del acta (orden del día con ids)
//   revision    { marcas: [{ puntoId, campo, motivo, evidencias }] }
//   ia          { evidenciasPorPunto, duracionSeg } | null
//   asambleaId  id de la asamblea (para pedir el audio); sin él no hay "Escuchar"
//   onIrAPunto  (puntoId) => void
//   onCambiarMarca (indice, { estadoRevision?, cerrada? }) => void  seguimiento de una marca
//                  (índice en revision.marcas); sin él, o con readOnly, no hay botones
//   readOnly    acta publicada o bloqueada

const VIGENCIA_URL_MS = 9 * 60 * 1000; // la URL firmada dura 10 min: margen de 1

export default function ActaRevisionPanel({ acta, revision, ia, asambleaId, onIrAPunto, onCambiarMarca, readOnly = false }) {
  const audioRef = useRef(null);
  const urlRef = useRef({ url: null, venceMs: 0 });
  const [sonando, setSonando] = useState(null); // { clave, inicioMs }
  const [cargandoClave, setCargandoClave] = useState(null);
  const [avisoAudio, setAvisoAudio] = useState("");
  const [abiertos, setAbiertos] = useState({}); // puntoId → citas visibles
  const [filtro, setFiltro] = useState("abiertas");

  const puntos = (acta && acta.ordenDelDia) || [];
  const idsPuntos = new Set(puntos.map((p) => String(p.id)));
  // Cada marca con su índice en revision.marcas (para guardar su seguimiento) y filtrada.
  const crudas = Array.isArray(revision && revision.marcas) ? revision.marcas : [];
  const conIndice = crudas.map((m, i) => (m ? { ...m, _i: i } : m));
  const filtroActivo = FILTROS_MARCAS.find((f) => f.id === filtro) || FILTROS_MARCAS[0];
  const visibles = { ...(revision || {}), marcas: conIndice.map((m) => (m && filtroActivo.incluye(m) ? m : null)) };
  const porPunto = marcasPorPunto(visibles);
  // Las marcas de un punto que ya no está en el acta (se quitó) van a "Generales".
  const huerfanas = [...porPunto.entries()].filter(([id]) => !idsPuntos.has(id)).flatMap(([, ms]) => ms);
  const generales = [...marcasGenerales(visibles), ...huerfanas];
  const total = totalMarcas(revision);
  const cuantas = (f) => crudas.filter((m) => m && f.incluye(m)).length;
  const conCitas = filtro === "abiertas";
  const editable = !readOnly && typeof onCambiarMarca === "function";
  const puedeEscuchar = !!asambleaId;

  // Si el componente se desmonta con audio sonando, se corta.
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (audio && typeof audio.pause === "function") {
        try {
          audio.pause();
        } catch (_) {
          /* jsdom no implementa pause */
        }
      }
    };
  }, []);

  const urlVigente = useCallback(
    async (inicioMs) => {
      const c = urlRef.current;
      if (c.url && Date.now() < c.venceMs) return c.url;
      const res = await actaAdmin("audioUrl", { aid: asambleaId, inicioMs });
      if (!res || !res.url) throw new Error("El servidor no devolvió el audio.");
      // Con headers (modo local: el emulador de Storage los pide) se baja y se reproduce como blob.
      const url = await urlDescargable(res);
      urlRef.current = { url, venceMs: Date.now() + VIGENCIA_URL_MS };
      return url;
    },
    [asambleaId]
  );

  const escuchar = async (ev, clave) => {
    const audio = audioRef.current;
    if (!audio || !puedeEscuchar) return;
    setAvisoAudio("");
    setCargandoClave(clave);
    try {
      const inicioMs = Math.max(0, Number(ev.inicioMs) || 0);
      const segundos = Math.floor(inicioMs / 1000);
      const url = await urlVigente(inicioMs);
      if (audio.dataset.base !== url) {
        // #t= posiciona en los navegadores que respetan media fragments; el
        // currentTime al cargar cubre a los que no.
        audio.dataset.base = url;
        const alCargar = () => {
          audio.removeEventListener("loadedmetadata", alCargar);
          try {
            audio.currentTime = segundos;
          } catch (_) {
            /* sin metadata todavía */
          }
        };
        audio.addEventListener("loadedmetadata", alCargar);
        audio.src = `${url}#t=${segundos}`;
      } else {
        try {
          audio.currentTime = segundos;
        } catch (_) {
          /* sin metadata todavía */
        }
      }
      setSonando({ clave, inicioMs });
      const p = typeof audio.play === "function" ? audio.play() : null;
      if (p && typeof p.catch === "function") p.catch(() => setAvisoAudio("Tocá play en el reproductor para escuchar."));
    } catch (e) {
      setAvisoAudio(mensajeDeError(e));
    } finally {
      setCargandoClave(null);
    }
  };

  const alFallarAudio = () => {
    // Puede ser la URL vencida (pausa larga) o un formato que el navegador no lee.
    urlRef.current = { url: null, venceMs: 0 };
    if (audioRef.current) audioRef.current.dataset.base = "";
    setAvisoAudio("No pudimos reproducir el audio. Probá de nuevo; si sigue, usá Chrome o Firefox.");
  };

  const hayCitas = puntos.some((p) => evidenciasDePunto(p.id, revision, ia).length > 0);
  const propsMarca = { puedeEscuchar, sonando, cargandoClave, onEscuchar: escuchar, editable, onCambiar: onCambiarMarca };
  const nadaEnElFiltro = generales.length === 0 && ![...porPunto.values()].some((ms) => ms.length);

  return (
    <div style={S.panel} aria-label="Revisión del acta">
      <header style={S.header}>
        <h2 style={S.titulo}>Revisión</h2>
        <p style={S.sub}>
          La IA redacta y cita lo que se dijo; la asistencia, los votos y el quórum los confirmás vos.
          {ia && ia.duracionSeg ? ` Audio de ${formatoTiempo(Number(ia.duracionSeg) * 1000)}.` : ""}
        </p>
      </header>

      <div style={{ ...S.player, display: sonando ? "flex" : "none" }}>
        <span style={S.playerLabel}>
          {sonando ? `Escuchando desde ${formatoTiempo(sonando.inicioMs)}` : ""}
        </span>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio ref={audioRef} controls preload="none" style={S.audio} onError={alFallarAudio} aria-label="Audio de la asamblea" />
      </div>
      {avisoAudio && (
        <p style={S.aviso} role="alert">
          {avisoAudio}
        </p>
      )}

      {total === 0 && !hayCitas ? (
        <EmptyState icon={FaCheckCircle} title="Sin marcas para revisar" description="Esta acta no tiene marcas ni citas de la IA." />
      ) : (
        <>
          {total === 0 && (
            <p style={S.okLine}>
              <FaCheckCircle size={12} color={colors.success} aria-hidden="true" /> La IA no dejó marcas. Igual podés revisar sus citas.
            </p>
          )}

          {total > 0 && (
            <div style={S.filtros} role="group" aria-label="Filtrar marcas">
              {FILTROS_MARCAS.map((f) => {
                const activo = f.id === filtroActivo.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    style={{ ...S.filtro, ...(activo ? S.filtroActivo : {}) }}
                    onClick={() => setFiltro(f.id)}
                    aria-pressed={activo}
                  >
                    {f.id === "resueltas" && <FaCheckCircle size={11} color={colors.success} aria-hidden="true" />}
                    {f.id === "no_resueltas" && <FaTimesCircle size={11} color={colors.error} aria-hidden="true" />}
                    {f.label} ({cuantas(f)})
                  </button>
                );
              })}
            </div>
          )}
          {total > 0 && nadaEnElFiltro && <p style={S.okLine}>No hay marcas en «{filtroActivo.label}».</p>}

          {generales.length > 0 && (
            <section style={S.bloque}>
              <h3 style={S.bloqueTitulo}>Generales</h3>
              {generales.map((m) => (
                <Marca key={`g${m._i}`} marca={m} claveBase={`g${m._i}`} {...propsMarca} />
              ))}
            </section>
          )}

          {puntos.map((p, idx) => {
            const marcas = porPunto.get(String(p.id)) || [];
            const citas = conCitas ? evidenciasDePunto(p.id, revision, ia) : [];
            if (!marcas.length && !citas.length) return null;
            const abierto = !!abiertos[p.id];
            return (
              <section key={p.id} style={S.bloque}>
                <div style={S.puntoHead}>
                  <h3 style={S.bloqueTitulo}>
                    Punto {pad2(idx + 1)} · {p.titulo || "sin título"}
                  </h3>
                  {onIrAPunto && (
                    <button
                      type="button"
                      style={btnGhost}
                      className="habitia-btn-ghost"
                      onClick={() => onIrAPunto(p.id)}
                      aria-label={`Ir al punto ${idx + 1} en el formulario`}
                      title="Ir al punto"
                    >
                      <FaArrowRight size={12} />
                    </button>
                  )}
                </div>
                {marcas.map((m) => (
                  <Marca key={`${p.id}-${m._i}`} marca={m} claveBase={`${p.id}-m${m._i}`} {...propsMarca} sinCitas />
                ))}
                {citas.length > 0 && (
                  <>
                    <button
                      type="button"
                      style={S.toggle}
                      onClick={() => setAbiertos((s) => ({ ...s, [p.id]: !abierto }))}
                      aria-expanded={abierto}
                    >
                      {abierto ? "Ocultar citas" : `Ver citas (${citas.length})`}
                    </button>
                    {abierto &&
                      citas.map((ev, i) => (
                        <Cita
                          key={`${p.id}-c${i}`}
                          ev={ev}
                          clave={`${p.id}-c${i}`}
                          puedeEscuchar={puedeEscuchar}
                          sonando={sonando}
                          cargandoClave={cargandoClave}
                          onEscuchar={escuchar}
                        />
                      ))}
                  </>
                )}
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}

const TONO_MARCA = {
  resuelta: { bg: colors.successLight, icono: FaCheckCircle, color: colors.success, texto: colors.successDark },
  no_resuelta: { bg: colors.errorLight, icono: FaTimesCircle, color: colors.error, texto: colors.errorDark },
  pendiente: { bg: colors.warningLight, icono: FaExclamationTriangle, color: colors.warningDark, texto: colors.warningDark },
};

function Marca({ marca, claveBase, puedeEscuchar, sonando, cargandoClave, onEscuchar, sinCitas = false, editable, onCambiar }) {
  const evidencias = Array.isArray(marca.evidencias) ? marca.evidencias : [];
  const estado = estadoDeMarca(marca);
  const cerrada = marcaCerrada(marca);
  const tono = TONO_MARCA[estado || "pendiente"];
  const Icono = tono.icono;
  const nombre = etiquetaCampo(marca.campo) || "Revisar";
  // El mismo tick otra vez lo desmarca.
  const alternar = (e) => onCambiar(marca._i, { estadoRevision: estado === e ? null : e });
  return (
    <div style={{ ...S.marca, background: tono.bg, ...(cerrada ? S.marcaCerrada : {}) }}>
      <div style={S.marcaHead}>
        <Icono size={12} color={tono.color} aria-hidden="true" />
        <span style={{ ...S.marcaCampo, color: tono.texto }}>{nombre}</span>
        {estado && <span style={S.marcaEstado}>{estado === "resuelta" ? "Resuelta" : "No resuelta"}</span>}
        {editable && (
          <span style={S.marcaAcciones}>
            <button
              type="button"
              style={{ ...S.btnMarca, ...(estado === "resuelta" ? S.btnResuelta : {}) }}
              onClick={() => alternar("resuelta")}
              aria-pressed={estado === "resuelta"}
              aria-label={`Marcar como resuelta: ${nombre}`}
              title="Resuelta"
            >
              <FaCheckCircle size={13} />
            </button>
            <button
              type="button"
              style={{ ...S.btnMarca, ...(estado === "no_resuelta" ? S.btnNoResuelta : {}) }}
              onClick={() => alternar("no_resuelta")}
              aria-pressed={estado === "no_resuelta"}
              aria-label={`Marcar como no resuelta: ${nombre}`}
              title="No resuelta"
            >
              <FaTimesCircle size={13} />
            </button>
            <button
              type="button"
              style={S.btnMarca}
              onClick={() => onCambiar(marca._i, { cerrada: !cerrada })}
              aria-label={cerrada ? `Reabrir la marca: ${nombre}` : `Cerrar la marca: ${nombre}`}
              title={cerrada ? "Reabrir" : "Cerrar"}
            >
              {cerrada ? <FaUndo size={11} /> : <FaTimes size={12} />}
            </button>
          </span>
        )}
      </div>
      {marca.motivo && <p style={S.marcaMotivo}>{marca.motivo}</p>}
      {!sinCitas &&
        evidencias.map((ev, i) => (
          <Cita
            key={`${claveBase}-${i}`}
            ev={ev}
            clave={`${claveBase}-${i}`}
            puedeEscuchar={puedeEscuchar}
            sonando={sonando}
            cargandoClave={cargandoClave}
            onEscuchar={onEscuchar}
          />
        ))}
      {sinCitas && evidencias.length > 0 && (
        <Cita
          ev={evidencias[0]}
          clave={`${claveBase}-0`}
          puedeEscuchar={puedeEscuchar}
          sonando={sonando}
          cargandoClave={cargandoClave}
          onEscuchar={onEscuchar}
        />
      )}
    </div>
  );
}

function Cita({ ev, clave, puedeEscuchar, sonando, cargandoClave, onEscuchar }) {
  const activa = sonando && sonando.clave === clave;
  return (
    <div style={{ ...S.cita, ...(activa ? S.citaActiva : {}) }}>
      <FaQuoteLeft size={10} color={colors.gray400} aria-hidden="true" style={{ flexShrink: 0, marginTop: 4 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={S.citaTexto}>{ev.cita}</p>
        <span style={S.citaTiempo}>{formatoTiempo(ev.inicioMs)}</span>
      </div>
      {puedeEscuchar && (
        <button
          type="button"
          style={S.btnEscuchar}
          className="habitia-btn-secondary"
          onClick={() => onEscuchar(ev, clave)}
          disabled={cargandoClave === clave}
          aria-label={`Escuchar desde ${formatoTiempo(ev.inicioMs)}`}
        >
          <FaPlay size={9} /> {cargandoClave === clave ? "…" : "Escuchar"}
        </button>
      )}
    </div>
  );
}

const S = {
  panel: {
    padding: `${spacing[5]}px ${spacing[4]}px ${spacing[8]}px`,
    display: "flex",
    flexDirection: "column",
    gap: spacing[3],
    fontFamily: typography.fontFamily,
    fontWeight: 400,
  },
  header: { display: "flex", flexDirection: "column", gap: spacing[1] },
  titulo: { margin: 0, fontSize: typography.sizes.lg, color: colors.black, fontWeight: 400 },
  sub: { margin: 0, fontSize: typography.sizes.sm, color: colors.gray600, lineHeight: 1.5 },
  player: {
    flexDirection: "column",
    gap: spacing[1],
    background: colors.gray50,
    borderRadius: radius.sm,
    padding: spacing[2],
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  playerLabel: { fontSize: typography.sizes.sm, color: colors.gray700 },
  audio: { width: "100%" },
  aviso: { margin: 0, fontSize: typography.sizes.sm, color: colors.errorDark },
  okLine: { margin: 0, fontSize: typography.sizes.sm, color: colors.gray700, display: "flex", alignItems: "center", gap: spacing[2] },
  bloque: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[2],
    paddingBottom: spacing[3],
    borderBottom: `1px solid ${colors.gray100}`,
  },
  puntoHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: spacing[2] },
  bloqueTitulo: { margin: 0, fontSize: typography.sizes.md, color: colors.black, fontWeight: 400 },
  marca: {
    background: colors.warningLight,
    borderRadius: radius.sm,
    padding: `${spacing[2]}px ${spacing[3]}px`,
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
  },
  marcaHead: { display: "flex", alignItems: "center", gap: spacing[2] },
  marcaCampo: { fontSize: typography.sizes.sm, color: colors.warningDark, textTransform: "uppercase", letterSpacing: "0.04em" },
  marcaEstado: { fontSize: typography.sizes.xs, color: colors.gray700 },
  marcaCerrada: { opacity: 0.75 },
  marcaAcciones: { marginLeft: "auto", display: "inline-flex", gap: spacing[1] },
  btnMarca: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: radius.sm,
    border: `1px solid ${colors.gray300}`,
    background: colors.white,
    color: colors.gray600,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  // Sin mezclar border con borderColor (React avisa al alternar): el borde entero.
  btnResuelta: { color: colors.success, border: `1px solid ${colors.success}` },
  btnNoResuelta: { color: colors.error, border: `1px solid ${colors.error}` },
  filtros: { display: "flex", flexWrap: "wrap", gap: spacing[1] },
  filtro: {
    display: "inline-flex",
    alignItems: "center",
    gap: spacing[1],
    padding: "4px 10px",
    borderRadius: radius.full,
    border: `1px solid ${colors.gray300}`,
    background: colors.white,
    color: colors.gray700,
    fontSize: typography.sizes.sm,
    fontFamily: typography.fontFamily,
    fontWeight: 400,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  filtroActivo: { background: colors.primarySubtle, color: colors.primaryDark, border: `1px solid ${colors.primaryLight}` },
  marcaMotivo: { margin: 0, fontSize: typography.sizes.base, color: colors.gray900, lineHeight: 1.5 },
  toggle: {
    alignSelf: "flex-start",
    background: "transparent",
    border: 0,
    padding: 0,
    color: colors.primaryDark,
    cursor: "pointer",
    fontFamily: typography.fontFamily,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    textDecoration: "underline",
    transition: `color ${transitions.fast}`,
  },
  cita: {
    display: "flex",
    alignItems: "flex-start",
    gap: spacing[2],
    background: colors.white,
    borderRadius: radius.sm,
    padding: spacing[2],
    transition: `background ${transitions.fast}`,
  },
  citaActiva: { background: colors.primaryLighter },
  citaTexto: { margin: 0, fontSize: typography.sizes.base, color: colors.gray700, lineHeight: 1.5, fontStyle: "italic", overflowWrap: "anywhere" },
  citaTiempo: { fontSize: typography.sizes.xs, color: colors.gray600, fontVariantNumeric: "tabular-nums" },
  btnEscuchar: {
    display: "inline-flex",
    alignItems: "center",
    gap: spacing[1],
    flexShrink: 0,
    padding: "4px 10px",
    borderRadius: radius.sm,
    border: `1px solid ${colors.gray200}`,
    background: colors.white,
    color: colors.gray900,
    fontSize: typography.sizes.sm,
    fontFamily: typography.fontFamily,
    fontWeight: 400,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
};
